// web-common/src/locale-cookie.js — the two-cookie locale contract (D-AUTH §3), a faithful
// JS port of Quocia-FB's src/lib/i18n/locale-cookie.ts (the reference file IS the spec —
// its comments carried over where they carry the law).
//
// - `lang` — the PUBLIC-site preference, shared across the quocia.com estate via
//   Domain=<sharedDomain> (config: VITE_SHARED_COOKIE_DOMAIN = '.quocia.com' on the real
//   domains; UNSET on localhost/web.app/quicketing.com, where the cookie degrades to
//   host-only and resolution falls through to the browser language → default). The
//   marketing site sets it; the app READS it signed-out and SEEDS it when missing on
//   auth pages (the reference middleware's UAU seeding, translated to the SPA).
//
// - `userLang` — the AUTHENTICATED user's UI locale, host-only on this app's domain,
//   mirroring the account's users.ui_locale. Deliberately non-httpOnly: it carries no
//   authority, it's purely the UI-locale hint. WHY a client-side writer exists (the
//   reference's 2026-06-10 "language change doesn't stick" bug): a server refresh can
//   decline or fail transiently; if the cookie then stays stale, everything that mounts
//   after the one-shot locale broadcast falls back to the wrong language. LocaleSync
//   calls writeUserLangCookie to keep the cookie in lockstep with the authoritative
//   account locale — the UI language never depends on a server round-trip succeeding.
//   Cleared on sign-out.
//
// Resolution rule for the app's UI: `userLang` wins when present, else `lang`, else the
// default. Existence of userLang implies the user is (or was very recently)
// authenticated, so the account preference wins; otherwise honor the device-level
// public choice. Auth-flow uauLocale lookups use getPublicLocaleFromCookies instead —
// they seed the ACCOUNT language from the user's *public* preference at signup.

const YEAR = 60 * 60 * 24 * 365;

export function readCookieValue(name) {
  if (typeof document === 'undefined') return undefined;
  const row = document.cookie.split('; ').find((r) => r.startsWith(`${name}=`));
  if (!row) return undefined;
  // other writers may percent-encode; locale tags don't need it, decode defensively
  try { return decodeURIComponent(row.slice(name.length + 1)); }
  catch { return row.slice(name.length + 1); }
}

function asSupported(tags, value) {
  return value && tags.includes(value) ? value : undefined;
}

// userLang → lang → null (callers supply the default so the registry stays the truth)
export function getLocaleFromCookies(tags) {
  return asSupported(tags, readCookieValue('userLang'))
    ?? asSupported(tags, readCookieValue('lang'))
    ?? null;
}

// lang → null — the auth-flow uauLocale read (public device preference ONLY)
export function getPublicLocaleFromCookies(tags) {
  return asSupported(tags, readCookieValue('lang')) ?? null;
}

// host-only, Path=/, 1 year, SameSite=Lax, Secure on https — the reference attributes,
// byte-for-byte, so it overwrites the same cookie a future server writer would set.
export function writeUserLangCookie(locale) {
  if (typeof document === 'undefined') return;
  const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `userLang=${locale}; Path=/; Max-Age=${YEAR}; SameSite=Lax${secure}`;
}

export function clearUserLangCookie() {
  if (typeof document === 'undefined') return;
  const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `userLang=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
}

// the shared public cookie. domain comes from CONFIG (never hardcoded): pass
// VITE_SHARED_COOKIE_DOMAIN; when absent (localhost, *.web.app, the quicketing.com admin)
// the Domain attribute is omitted and the cookie is host-only — the graceful fallback.
export function writeLangCookie(locale, { domain = null } = {}) {
  if (typeof document === 'undefined') return;
  const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
  const dom = domain ? `; Domain=${domain}` : '';
  document.cookie = `lang=${locale}; Path=/; Max-Age=${YEAR}; SameSite=Lax${dom}${secure}`;
}

// the SPA's translation of the reference middleware's UAU seeding: on auth-page mount,
// a missing/unsupported `lang` cookie is written with the resolved device preference
// (browser language → default) so the whole estate shares one signed-out language from
// then on. Returns the tag that now governs.
export function seedLangCookieIfMissing(tags, { domain = null, resolve } = {}) {
  const existing = getPublicLocaleFromCookies(tags);
  if (existing) return existing;
  const tag = resolve(); // pickInitialLocale(registry, null) — browser → default
  writeLangCookie(tag, { domain });
  return tag;
}
